// components/admin/MaterialsTab.js - Updated with improved grid layout and tile design
import React, { useState, useEffect, useCallback } from "react"
import { SketchPicker } from "react-color"
import {
  fetchMaterials,
  addMaterial,
  updateMaterial,
  deleteMaterial,
  uploadFile,
} from "../../api"

const MaterialsTab = ({ setLoading }) => {
  // Materials state
  const [materialsData, setMaterialsData] = useState({ materials: [] })
  const [filteredMaterials, setFilteredMaterials] = useState([])

  // Filters
  const [activeType, setActiveType] = useState("all")
  const [activeCollection, setActiveCollection] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")

  // UI states
  const [showMaterialForm, setShowMaterialForm] = useState(false)
  const [editingMaterial, setEditingMaterial] = useState(null)
  const [view, setView] = useState("grid")

  // Material form data
  const [materialFormData, setMaterialFormData] = useState({
    id: "",
    name: "",
    slug: "",
    hover: "",
    thumbnail: "",
    type: "struttura",
    collection: "metallo",
  })

  // Material thumbnail states
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [thumbnailPreview, setThumbnailPreview] = useState("")
  const [thumbnailUpload, setThumbnailUpload] = useState(null)

  // Cleaning states
  const [duplicatesFound, setDuplicatesFound] = useState([])
  const [showCleaningTools, setShowCleaningTools] = useState(false)

  // Load initial data
  useEffect(() => {
    loadMaterials()
  }, [])

  // Apply filters whenever materials or filter criteria change
  useEffect(() => {
    applyFilters()
  }, [materialsData, activeType, activeCollection, searchTerm])

  // Load all materials
  const loadMaterials = async () => {
    setLoading(true)
    try {
      const data = await fetchMaterials()
      setMaterialsData(data)
      findProblematicMaterials(data.materials)
    } catch (error) {
      console.error("Failed to load materials:", error)
    } finally {
      setLoading(false)
    }
  }

  // Find problematic materials with enhanced detection
  const findProblematicMaterials = (materials) => {
    const materialsByName = {}
    const problematicEntries = []

    materials.forEach((material) => {
      const cleanName = material.name.trim().replace(/\s+/g, " ")
      if (!materialsByName[cleanName]) {
        materialsByName[cleanName] = []
      }
      materialsByName[cleanName].push(material)
    })

    // Find materials with same name but different slugs
    Object.entries(materialsByName).forEach(([name, group]) => {
      if (group.length <= 1) return

      const uniqueSlugs = new Set(group.map((m) => m.slug.trim()))
      const hasSpaceIssues = group.some(
        (m) =>
          m.slug !== m.slug.trim() ||
          m.slug.includes("  ") ||
          m.name !== m.name.trim() ||
          m.name.includes("  ")
      )

      if (uniqueSlugs.size > 1 || hasSpaceIssues) {
        problematicEntries.push({
          name,
          materials: group,
          issue: uniqueSlugs.size === 1 ? "spacing_issues" : "different_slugs",
          details: group.map((m) => ({
            id: m.id,
            name: `"${m.name}"`,
            slug: `"${m.slug}"`,
            hasSpaces: m.slug !== m.slug.trim() || m.name !== m.name.trim(),
            hasDoubleSpaces: m.slug.includes("  ") || m.name.includes("  "),
          })),
        })
      } else if (uniqueSlugs.size === 1) {
        // True duplicates
        problematicEntries.push({
          name,
          materials: group,
          issue: "true_duplicates",
          details: group.map((m) => ({
            id: m.id,
            name: m.name,
            slug: m.slug,
          })),
        })
      }
    })

    setDuplicatesFound(problematicEntries)
  }

  // Apply filters to materials
  const applyFilters = useCallback(() => {
    let filtered = [...(materialsData.materials || [])]

    if (activeType !== "all") {
      filtered = filtered.filter((material) => material.type === activeType)
    }

    if (activeCollection !== "all") {
      filtered = filtered.filter(
        (material) => material.collection === activeCollection
      )
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (material) =>
          material.name.toLowerCase().includes(lowerSearchTerm) ||
          material.id.toString().toLowerCase().includes(lowerSearchTerm) ||
          material.slug.toLowerCase().includes(lowerSearchTerm) ||
          (material.collection &&
            material.collection.toLowerCase().includes(lowerSearchTerm))
      )
    }

    setFilteredMaterials(filtered)
  }, [materialsData, activeType, activeCollection, searchTerm])

  // Get all unique types and collections
  const getUniqueTypes = () => {
    const types = new Set()
    materialsData.materials.forEach((material) => {
      if (material.type) types.add(material.type)
    })
    return Array.from(types).sort()
  }

  const getUniqueCollections = () => {
    const collections = new Set()
    materialsData.materials.forEach((material) => {
      if (material.collection) collections.add(material.collection)
    })
    return Array.from(collections).sort()
  }

  // Group materials by collection with smart grouping for display
  const getMaterialsByCollection = () => {
    const materialsByCollection = {}

    filteredMaterials.forEach((material) => {
      const collection = material.collection || "Senza Collezione"
      if (!materialsByCollection[collection]) {
        materialsByCollection[collection] = []
      }
      materialsByCollection[collection].push(material)
    })

    Object.keys(materialsByCollection).forEach((collection) => {
      materialsByCollection[collection].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    })

    return materialsByCollection
  }

  // Group collections for optimal display with separate containers
  const getCollectionGroups = () => {
    const materialsByCollection = getMaterialsByCollection()
    const sortedCollections = Object.entries(materialsByCollection).sort(
      ([a], [b]) => a.localeCompare(b)
    )

    const groups = []
    let currentRow = []
    let currentRowItemCount = 0

    sortedCollections.forEach(([collection, materials]) => {
      const itemCount = materials.length

      // If collection has more than 10 items, it gets its own row
      if (itemCount > 10) {
        // Finish current row if it has items
        if (currentRow.length > 0) {
          groups.push(currentRow)
          currentRow = []
          currentRowItemCount = 0
        }
        // Add the large collection as its own row
        groups.push([[collection, materials]])
      } else {
        // Check if adding this collection would exceed 10 items
        if (currentRowItemCount + itemCount > 10 && currentRow.length > 0) {
          // Finish current row and start new one
          groups.push(currentRow)
          currentRow = [[collection, materials]]
          currentRowItemCount = itemCount
        } else {
          // Add to current row
          currentRow.push([collection, materials])
          currentRowItemCount += itemCount
        }
      }
    })

    // Add remaining row if it has items
    if (currentRow.length > 0) {
      groups.push(currentRow)
    }

    return groups
  }

  // Render slug info with enhanced problem detection
  const renderSlugInfo = (material) => {
    const { slug, name } = material
    if (!slug) return <span className="ay-text-gray-400 ay-text-xs">-</span>

    const cleanSlug = slug.trim()
    const cleanName = name.trim()

    // Check for various spacing issues
    const hasLeadingTrailingSpaces =
      slug !== slug.trim() || name !== name.trim()
    const hasDoubleSpaces = slug.includes("  ") || name.includes("  ")
    const hasWeirdSpaces = hasLeadingTrailingSpaces || hasDoubleSpaces

    if (hasWeirdSpaces) {
      return (
        <span
          className="ay-text-red-600 ay-font-mono ay-bg-red-50 ay-px-1 ay-rounded ay-text-xs"
          title="Attenzione: spazi irregolari"
        >
          {slug.replace(/ /g, "·")} ⚠️
        </span>
      )
    }

    // If slug is identical to name, show it less prominently
    if (cleanSlug === cleanName) {
      return (
        <span
          className="ay-text-blue-400 ay-text-xs ay-opacity-75"
          title="Slug identico al nome"
        >
          {cleanSlug}
        </span>
      )
    }

    // Different slug from name - show normally
    return (
      <span className="ay-text-gray-600 ay-text-xs ay-font-medium">
        {cleanSlug}
      </span>
    )
  }

  // Handle add material
  const handleAddMaterial = () => {
    setEditingMaterial(null)
    const maxId =
      materialsData.materials.length > 0
        ? Math.max(...materialsData.materials.map((m) => parseInt(m.id) || 0))
        : 0

    setMaterialFormData({
      id: (maxId + 1).toString(),
      name: "",
      slug: "",
      hover: "",
      thumbnail: "",
      type: "struttura",
      collection: "metallo",
    })
    setThumbnailPreview("")
    setThumbnailUpload(null)
    setShowMaterialForm(true)
  }

  // Handle edit material
  const handleEditMaterial = (material) => {
    setEditingMaterial(material)
    setMaterialFormData({
      id: material.id.toString(),
      name: material.name,
      slug: material.slug,
      hover: material.hover || "",
      thumbnail: material.thumbnail,
      type: material.type || "struttura",
      collection: material.collection || "metallo",
    })
    setThumbnailPreview(material.thumbnail)
    setThumbnailUpload(null)
    setShowMaterialForm(true)
  }

  // Handle delete material
  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo materiale?")) {
      return
    }
    setLoading(true)
    try {
      await deleteMaterial(materialId)
      await loadMaterials()
    } catch (error) {
      console.error("Failed to delete material:", error)
      alert(`Errore: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Handle material form change
  const handleMaterialFormChange = (e) => {
    const { name, value } = e.target
    setMaterialFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle color change in color picker
  const handleColorChange = (color) => {
    setMaterialFormData((prev) => ({
      ...prev,
      thumbnail: color.hex,
    }))
    setThumbnailPreview(color.hex)
  }

  // Handle thumbnail file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      setThumbnailUpload(file)
      const reader = new FileReader()
      reader.onloadend = () => setThumbnailPreview(reader.result)
      reader.readAsDataURL(file)
    }
  }

  // Handle material form submit
  const handleMaterialFormSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const formData = {
        ...materialFormData,
        id: parseInt(materialFormData.id) || materialFormData.id,
      }

      if (thumbnailUpload) {
        try {
          const uploadResult = await uploadFile(thumbnailUpload)
          formData.thumbnail = uploadResult.url
        } catch (uploadError) {
          console.error("Upload failed:", uploadError)
          alert("Errore nel caricamento dell'immagine")
          return
        }
      }

      if (editingMaterial) {
        await updateMaterial(formData)
      } else {
        await addMaterial(formData)
      }

      await loadMaterials()
      setShowMaterialForm(false)
    } catch (error) {
      console.error("Failed to save material:", error)
      alert(`Errore: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Clean problematic materials
  const cleanProblematicMaterials = async (group) => {
    const message =
      group.issue === "true_duplicates"
        ? `Vuoi mantenere "${group.materials[0].name}" (ID: ${
            group.materials[0].id
          }) ed eliminare ${group.materials.length - 1} duplicati?`
        : `Trovati materiali con problema "${group.name}". Vuoi mantenere "${
            group.materials[0].name
          }" (ID: ${group.materials[0].id}) ed eliminare gli altri ${
            group.materials.length - 1
          }?`

    if (!window.confirm(message)) {
      return
    }

    setLoading(true)
    try {
      for (let i = 1; i < group.materials.length; i++) {
        await deleteMaterial(group.materials[i].id)
      }
      await loadMaterials()
      alert("Pulizia completata!")
    } catch (error) {
      console.error("Failed to clean materials:", error)
      alert(`Errore: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Render tile with background and overlay
  const renderMaterialTile = (material, index, collectionName) => {
    const backgroundStyle = material.thumbnail
      ? material.thumbnail.startsWith("#") ||
        material.thumbnail.startsWith("rgb")
        ? { backgroundColor: material.thumbnail }
        : {
            backgroundImage: `url(${material.thumbnail})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
      : { backgroundColor: "#f3f4f6" }

    return (
      <div
        key={`${collectionName}-${material.id}-${index}`}
        className="ay-relative ay-border ay-border-gray-200 ay-rounded ay-overflow-hidden ay-hover:ay-shadow-lg ay-transition-[box-shadow] ay-duration-300 ay-h-32"
        style={backgroundStyle}
      >
        {/* Gradient Overlay */}
        <div className="ay-absolute ay-inset-0 ay-bg-gradient-to-t ay-from-black/70 ay-via-black/30 ay-to-transparent"></div>

        {/* Content */}
        <div className="ay-relative ay-h-full ay-p-2 ay-flex ay-flex-col ay-justify-end">
          {/* Material Info */}
          <div className="ay-text-white ay-space-y-1">
            <p
              className="ay-font-medium ay-text-xs ay-leading-tight ay-truncate"
              title={material.name}
            >
              {material.name}
            </p>
            <p className="ay-text-xs ay-font-mono ay-opacity-90">
              "{material.slug}"
            </p>
            {/* Hover text if available */}
            {material.hover && (
              <p className="ay-text-xs ay-italic ay-opacity-85 ay-truncate">
                {material.hover}
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="ay-absolute ay-top-1 ay-right-1 ay-flex ay-gap-1 ay-opacity-0 hover:ay-opacity-100 ay-transition-opacity">
          <button
            onClick={() => handleEditMaterial(material)}
            className="ay-text-xs ay-p-1 ay-bg-blue-500/80 ay-text-white ay-rounded ay-hover:ay-bg-blue-600 ay-w-5 ay-h-5 ay-flex ay-items-center ay-justify-center"
            title="Modifica"
          >
            ✏
          </button>
          <button
            onClick={() => handleDeleteMaterial(material.id)}
            className="ay-text-xs ay-p-1 ay-bg-red-500/80 ay-text-white ay-rounded ay-hover:ay-bg-red-600 ay-w-5 ay-h-5 ay-flex ay-items-center ay-justify-center"
            title="Elimina"
          >
            🗑
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Material Form Modal */}
      {showMaterialForm && (
        <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
          <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-2xl ay-max-h-screen ay-overflow-auto">
            <h3 className="ay-text-lg ay-font-bold ay-mb-4">
              {editingMaterial ? "Modifica Materiale" : "Aggiungi Materiale"}
            </h3>
            <form onSubmit={handleMaterialFormSubmit}>
              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4 ay-mb-4">
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    ID Materiale *
                  </label>
                  <input
                    type="text"
                    name="id"
                    value={materialFormData.id}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Nome Materiale *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={materialFormData.name}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Slug (Nome Completo) *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={materialFormData.slug}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Hover (Testo Hover)
                  </label>
                  <input
                    type="text"
                    name="hover"
                    value={materialFormData.hover}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                    placeholder="es. Pure white"
                  />
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Tipo
                  </label>
                  <select
                    name="type"
                    value={materialFormData.type}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                  >
                    {getUniqueTypes().map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Collezione
                  </label>
                  <select
                    name="collection"
                    value={materialFormData.collection}
                    onChange={handleMaterialFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                  >
                    {getUniqueCollections().map((collection) => (
                      <option key={collection} value={collection}>
                        {collection}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ay-mb-6">
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
                  Anteprima
                </label>
                <div className="ay-space-y-3">
                  <div className="ay-flex ay-gap-2 ay-items-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="ay-block ay-w-full ay-text-sm ay-text-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className="ay-px-3 ay-py-1 ay-bg-gray-200 ay-rounded ay-text-sm"
                    >
                      🎨 Colore
                    </button>
                  </div>
                  {showColorPicker && (
                    <div className="ay-relative">
                      <SketchPicker
                        color={materialFormData.thumbnail}
                        onChange={handleColorChange}
                      />
                    </div>
                  )}
                </div>
                <div className="ay-mt-3">
                  <p className="ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Anteprima:
                  </p>
                  {thumbnailPreview ? (
                    thumbnailPreview.startsWith("#") ||
                    thumbnailPreview.startsWith("rgb") ? (
                      <div
                        className="ay-w-12 ay-h-12 ay-rounded ay-border ay-border-gray-300"
                        style={{ backgroundColor: thumbnailPreview }}
                      ></div>
                    ) : (
                      <img
                        src={thumbnailPreview}
                        alt="Preview"
                        className="ay-w-12 ay-h-12 ay-object-cover ay-rounded ay-border ay-border-gray-300"
                      />
                    )
                  ) : (
                    <div className="ay-w-12 ay-h-12 ay-bg-gray-200 ay-rounded ay-border ay-border-gray-300 ay-flex ay-items-center ay-justify-center">
                      <span className="ay-text-xs ay-text-gray-500">
                        Nessuna
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="ay-flex ay-justify-end ay-gap-3">
                <button
                  type="button"
                  onClick={() => setShowMaterialForm(false)}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-bg-blue-500 ay-text-white ay-hover:ay-bg-blue-600"
                >
                  {editingMaterial ? "Aggiorna" : "Crea"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div>
        {/* Header with actions */}
        <div className="ay-flex ay-justify-between ay-items-center ay-mb-4">
          <h2 className="ay-text-xl ay-font-bold">Gestione Materiali</h2>
          <div className="ay-flex ay-gap-2">
            <button
              onClick={() => setShowCleaningTools(!showCleaningTools)}
              className={`ay-px-3 ay-py-1.5 ay-text-sm ay-rounded ${
                duplicatesFound.length > 0
                  ? "ay-bg-yellow-500 ay-text-white ay-hover:ay-bg-yellow-600"
                  : "ay-bg-gray-300 ay-text-gray-600"
              }`}
            >
              🧹 Pulizia ({duplicatesFound.length})
            </button>
            <button
              onClick={handleAddMaterial}
              className="ay-px-3 ay-py-1.5 ay-text-sm ay-bg-blue-500 ay-text-white ay-rounded ay-hover:ay-bg-blue-600"
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>

        {/* Enhanced Cleaning Tools */}
        {showCleaningTools && duplicatesFound.length > 0 && (
          <div className="ay-mb-4 ay-p-3 ay-bg-yellow-50 ay-rounded ay-border">
            <h3 className="ay-text-sm ay-font-medium ay-mb-3">
              🧹 Problemi Rilevati ({duplicatesFound.length})
            </h3>
            <div className="ay-space-y-3">
              {duplicatesFound.map((group, index) => (
                <div
                  key={index}
                  className="ay-bg-white ay-rounded ay-border ay-p-3"
                >
                  <div className="ay-flex ay-justify-between ay-items-start ay-mb-2">
                    <div>
                      <span className="ay-font-medium ay-text-sm">
                        {group.name}
                      </span>
                      <span className="ay-text-xs ay-text-gray-600 ay-ml-2">
                        ({group.materials.length} elementi -{" "}
                        {group.issue === "true_duplicates"
                          ? "duplicati identici"
                          : group.issue === "spacing_issues"
                          ? "problemi di spaziatura"
                          : "slug diversi"}
                        )
                      </span>
                    </div>
                    <button
                      onClick={() => cleanProblematicMaterials(group)}
                      className="ay-px-2 ay-py-1 ay-bg-red-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-red-600"
                    >
                      Pulisci
                    </button>
                  </div>

                  {/* Detailed view of problems */}
                  <div className="ay-bg-gray-50 ay-rounded ay-p-2">
                    <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-2 ay-text-xs">
                      {group.details.map((detail, detailIndex) => (
                        <div
                          key={detailIndex}
                          className={`ay-p-2 ay-rounded ay-border ${
                            detail.hasSpaces || detail.hasDoubleSpaces
                              ? "ay-bg-red-50 ay-border-red-200"
                              : "ay-bg-white ay-border-gray-200"
                          }`}
                        >
                          <div className="ay-font-mono">
                            <div>
                              ID:{" "}
                              <span className="ay-font-medium">
                                {detail.id}
                              </span>
                            </div>
                            <div>
                              Nome:{" "}
                              <span
                                className={
                                  detail.hasSpaces ? "ay-text-red-600" : ""
                                }
                              >
                                {detail.name}
                              </span>
                            </div>
                            <div>
                              Slug:{" "}
                              <span
                                className={
                                  detail.hasSpaces ? "ay-text-red-600" : ""
                                }
                              >
                                {detail.slug}
                              </span>
                            </div>
                            {(detail.hasSpaces || detail.hasDoubleSpaces) && (
                              <div className="ay-text-red-600 ay-text-xs ay-mt-1">
                                ⚠️{" "}
                                {detail.hasDoubleSpaces
                                  ? "Spazi doppi"
                                  : "Spazi irregolari"}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="ay-mb-4 ay-p-3 ay-bg-gray-50 ay-rounded">
          <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-4 ay-gap-3">
            <div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ay-block ay-w-full ay-px-2 ay-py-1.5 ay-text-sm ay-border ay-border-gray-300 ay-rounded"
                placeholder="Cerca..."
              />
            </div>

            <div>
              <select
                value={activeType}
                onChange={(e) => setActiveType(e.target.value)}
                className="ay-block ay-w-full ay-px-2 ay-py-1.5 ay-text-sm ay-border ay-border-gray-300 ay-rounded"
              >
                <option value="all">Tutti i Tipi</option>
                {getUniqueTypes().map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={activeCollection}
                onChange={(e) => setActiveCollection(e.target.value)}
                className="ay-block ay-w-full ay-px-2 ay-py-1.5 ay-text-sm ay-border ay-border-gray-300 ay-rounded"
              >
                <option value="all">Tutte le Collezioni</option>
                {getUniqueCollections().map((collection) => (
                  <option key={collection} value={collection}>
                    {collection}
                  </option>
                ))}
              </select>
            </div>

            <div className="ay-flex ay-border ay-border-gray-300 ay-rounded ay-overflow-hidden">
              <button
                onClick={() => setView("grid")}
                className={`ay-px-2 ay-py-1.5 ay-text-sm ay-flex-1 ${
                  view === "grid"
                    ? "ay-bg-blue-500 ay-text-white"
                    : "ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                }`}
              >
                📊
              </button>
              <button
                onClick={() => setView("table")}
                className={`ay-px-2 ay-py-1.5 ay-text-sm ay-flex-1 ${
                  view === "table"
                    ? "ay-bg-blue-500 ay-text-white"
                    : "ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                }`}
              >
                📋
              </button>
            </div>
          </div>
        </div>

        {/* Materials Display */}
        {view === "grid" ? (
          <div className="ay-space-y-4">
            {getCollectionGroups().map((collectionRow, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className="ay-flex ay-gap-4 ay-flex-wrap"
              >
                {collectionRow.map(([collection, materials]) => {
                  // Calculate flex basis based on number of items (min-width but can grow)
                  const itemCount = materials.length
                  const minWidth = `${Math.max(itemCount * 10, 30)}%` // Minimum 30% width

                  return (
                    <div
                      key={`collection-${collection}`}
                      className="ay-border ay-rounded ay-overflow-hidden ay-shadow-sm ay-flex-grow"
                      style={{
                        minWidth:
                          itemCount <= 3
                            ? "30%"
                            : itemCount <= 6
                            ? "45%"
                            : "100%",
                        maxWidth: itemCount > 8 ? "100%" : "none",
                      }}
                    >
                      {/* Collection header */}
                      <div className="ay-bg-gray-100 ay-px-3 ay-py-2 ay-border-b">
                        <span className="ay-text-sm ay-font-medium ay-text-gray-800">
                          {collection} ({materials.length})
                        </span>
                      </div>

                      {/* Materials grid */}
                      <div className="ay-p-3">
                        <div
                          className="ay-grid ay-gap-3"
                          style={{
                            gridTemplateColumns: `repeat(${Math.min(
                              materials.length,
                              5
                            )}, 1fr)`,
                          }}
                        >
                          {materials.map((material, index) =>
                            renderMaterialTile(material, index, collection)
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        ) : (
          // Table View
          <div className="ay-bg-white ay-shadow ay-rounded-lg ay-overflow-hidden">
            <table className="ay-min-w-full ay-divide-y ay-divide-gray-200">
              <thead className="ay-bg-gray-50">
                <tr>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    ID
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Preview
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Nome
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Slug
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Hover
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Tipo/Collezione
                  </th>
                  <th className="ay-px-4 ay-py-2 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="ay-bg-white ay-divide-y ay-divide-gray-200">
                {filteredMaterials.length > 0 ? (
                  filteredMaterials.map((material, index) => (
                    <tr
                      key={`table-${material.id}-${index}`}
                      className="ay-hover:ay-bg-gray-50"
                    >
                      <td className="ay-px-4 ay-py-2 ay-text-sm ay-text-gray-900">
                        {material.id}
                      </td>
                      <td className="ay-px-4 ay-py-2">
                        {material.thumbnail ? (
                          material.thumbnail.startsWith("#") ||
                          material.thumbnail.startsWith("rgb") ? (
                            <div
                              className="ay-w-6 ay-h-6 ay-rounded ay-border ay-border-gray-300"
                              style={{ backgroundColor: material.thumbnail }}
                            ></div>
                          ) : (
                            <img
                              src={material.thumbnail}
                              alt={material.name}
                              className="ay-w-6 ay-h-6 ay-object-cover ay-rounded ay-border ay-border-gray-300"
                            />
                          )
                        ) : (
                          <div className="ay-w-6 ay-h-6 ay-bg-gray-200 ay-rounded ay-border ay-border-gray-300"></div>
                        )}
                      </td>
                      <td className="ay-px-4 ay-py-2">
                        <div className="ay-text-sm ay-font-medium ay-text-gray-900">
                          {material.name}
                        </div>
                      </td>
                      <td className="ay-px-4 ay-py-2">
                        {renderSlugInfo(material)}
                      </td>
                      <td className="ay-px-4 ay-py-2 ay-text-sm ay-text-gray-600 ay-italic">
                        {material.hover || "-"}
                      </td>
                      <td className="ay-px-4 ay-py-2 ay-text-sm ay-text-gray-500">
                        <div>
                          <div className="ay-font-medium">
                            {material.type || "Default"}
                          </div>
                          <div className="ay-text-xs">
                            {material.collection || "Default"}
                          </div>
                        </div>
                      </td>
                      <td className="ay-px-4 ay-py-2 ay-text-sm ay-font-medium">
                        <button
                          onClick={() => handleEditMaterial(material)}
                          className="ay-text-blue-600 ay-hover:ay-text-blue-900 ay-mr-2"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteMaterial(material.id)}
                          className="ay-text-red-600 ay-hover:ay-text-red-900"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="ay-px-4 ay-py-4 ay-text-center ay-text-gray-500 ay-text-sm"
                    >
                      Nessun materiale trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Materials Count */}
        <div className="ay-mt-3 ay-text-center ay-text-sm ay-text-gray-500">
          {filteredMaterials.length} di {materialsData.materials.length}{" "}
          materiali
        </div>
      </div>
    </div>
  )
}

export default MaterialsTab
